import { Badge, Card, SimpleGrid, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconCircleCheckFilled } from '@tabler/icons-react';
import { ComponentType, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { trackFeaturePreviewViewed } from 'src/Services/Mixpanel/events';

import { FEATURE_PREVIEW_CONFIG } from './FeaturePreview.config';
import styles from './FeaturePreview.module.scss';
import { Props } from './FeaturePreview.types';
import { FeatureSurvey } from './FeatureSurvey';

type IconComponent = ComponentType<{ size?: number; className?: string }>;

const FeaturePreview = ({ moduleKey }: Props) => {
  const { t } = useTranslation();
  const config = FEATURE_PREVIEW_CONFIG[moduleKey];
  const prefix = config.translationPrefix;
  const HeroIcon = config.icon as IconComponent;

  useEffect(() => {
    trackFeaturePreviewViewed({ module: moduleKey });
  }, [moduleKey]);

  const pains = Array.from({ length: config.painsCount }, (_, i) => t(`${prefix}.pain${i + 1}`));

  const benefits = Array.from({ length: config.benefitsCount }, (_, i) => ({
    Icon: config.benefitIcons[i] as IconComponent,
    title: t(`${prefix}.benefit${i + 1}Title`),
    text: t(`${prefix}.benefit${i + 1}Text`),
  }));

  const features = Array.from({ length: config.featuresCount }, (_, i) => ({
    title: t(`${prefix}.feat${i + 1}Title`),
    text: t(`${prefix}.feat${i + 1}Text`),
  }));

  const stories = Array.from({ length: config.storiesCount }, (_, i) =>
    t(`${prefix}.story${i + 1}`)
  );

  return (
    <div className={styles.container}>
      {/* 1. Hero */}
      <section className={styles.hero}>
        <div className={styles.heroIconWrap}>
          <HeroIcon size={36} />
        </div>
        <Badge variant="light" color="blue" size="lg">
          {t('featurePreview.badge')}
        </Badge>
        <Title order={1} className={styles.heroTitle}>
          {t(`${prefix}.title`)}
        </Title>
        <Text size="lg" className={styles.heroLede}>
          {t(`${prefix}.lede`)}
        </Text>
      </section>

      {/* 2. Bandeau statut */}
      <div className={styles.statusBanner}>
        <Text>{t('featurePreview.statusBanner')}</Text>
      </div>

      {/* 3. Aujourd'hui, sans ce module */}
      <section className={styles.section}>
        <Title order={2} className={styles.sectionHeading}>
          {t('featurePreview.painsTitle')}
        </Title>
        <Text className={styles.sectionSubtitle}>{t('featurePreview.painsSubtitle')}</Text>
        <ul className={styles.painsList}>
          {pains.map((pain, index) => (
            <li key={index} className={styles.painItem}>
              <IconAlertCircle size={20} className={styles.painBullet} />
              <Text>{pain}</Text>
            </li>
          ))}
        </ul>
      </section>

      {/* 4. Ce que ça change pour vous */}
      <section className={styles.section}>
        <Title order={2} className={styles.sectionHeading}>
          {t('featurePreview.benefitsTitle')}
        </Title>
        <Text className={styles.sectionSubtitle}>{t('featurePreview.benefitsSubtitle')}</Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {benefits.map(({ Icon, title, text }, index) => (
            <Card key={index} withBorder className={styles.benefitCard}>
              <div className={styles.benefitIconWrap}>
                <Icon size={22} />
              </div>
              <Text fw={600}>{title}</Text>
              <Text size="sm" c="dimmed">
                {text}
              </Text>
            </Card>
          ))}
        </SimpleGrid>
      </section>

      {/* 5. Concrètement, vous pourrez */}
      <section className={styles.section}>
        <Title order={2} className={styles.sectionHeading}>
          {t('featurePreview.featuresTitle')}
        </Title>
        <Text className={styles.sectionSubtitle}>{t('featurePreview.featuresSubtitle')}</Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="sm">
          {features.map(({ title, text }, index) => (
            <div key={index} className={styles.featureRow}>
              <IconCircleCheckFilled size={20} className={styles.featureBullet} />
              <div>
                <Text fw={600} span>
                  {title}
                </Text>{' '}
                <Text span c="dimmed">
                  — {text}
                </Text>
              </div>
            </div>
          ))}
        </SimpleGrid>
      </section>

      {/* 6. À quoi ça ressemble */}
      <section className={styles.section}>
        <Title order={2} className={styles.sectionHeading}>
          {t('featurePreview.storyTitle')}
        </Title>
        <Text className={styles.sectionSubtitle}>{t('featurePreview.storySubtitle')}</Text>
        <div className={styles.storyCard}>
          <Text className={styles.storyLabel}>{t(`${prefix}.storyLabel`)}</Text>
          {stories.map((story, index) => (
            <Text key={index}>{story}</Text>
          ))}
        </div>
      </section>

      {/* 7. Sondage */}
      <section className={styles.section}>
        <FeatureSurvey moduleKey={moduleKey} />
      </section>
    </div>
  );
};

export default FeaturePreview;
